/**
 * App-update announcement + startup gate. Live config `appUpdate.{minimalBuild, optionalBuild}`
 * decides: below minimal → blocking "update required", below optional → dismissible "what's new"
 * (once per session). The art (device with a fresh sheet sliding in, three items popping in turn)
 * is our own SVG/CSS.
 */
import { useState } from 'react';
import { appUpdateRequirement } from '@sketchling/core';
import { APP_BUILD, useApp } from '../state/store';
import { useT } from '../lib/i18n';
import { play } from '../lib/sfx';
import { Icon, type IconName } from '../components/icons';
import { Button } from '../components/ui';

const ITEMS: [IconName, 'ast.update.item1' | 'ast.update.item2' | 'ast.update.item3'][] = [
  ['book', 'ast.update.item1'],
  ['camera', 'ast.update.item2'],
  ['sticker', 'ast.update.item3'],
];

export function AppUpdateAnnouncement({ mode, onClose }: { mode: 'force' | 'optional'; onClose?: () => void }) {
  const t = useT();
  const update = () => {
    play('click');
    // Stores open from the native shell; on the web a reload fetches the new build.
    if (typeof window !== 'undefined') window.location.reload();
  };
  return (
    <div className="an-update" role="dialog" aria-modal="true" aria-label={t('ast.update.title')} data-testid="app-update" data-mode={mode}>
      <div className="an-up-art" aria-hidden>
        <svg viewBox="0 0 220 160" width="220" height="160">
          <g className="an-up-blobs">
            <circle cx="40" cy="40" r="34" fill="#2E7A8F" opacity="0.5" />
            <circle cx="190" cy="120" r="40" fill="#1F4E63" opacity="0.7" />
          </g>
          <rect x="50" y="20" width="120" height="124" rx="14" fill="#15394A" stroke="#FFFBF4" strokeWidth="4" />
          <rect className="an-up-sheet" x="62" y="34" width="96" height="96" rx="6" fill="#FFFBF4" />
          <path className="an-up-doodle" d="M80 104c8-30 26-34 32-14s18 16 26-8" fill="none" stroke="#FF7A1A" strokeWidth="5" strokeLinecap="round" pathLength={1} />
          <g className="an-up-badge">
            <circle cx="168" cy="26" r="18" fill="#FFC23A" stroke="#132630" strokeWidth="3" />
            <path d="M168 16v14M161 24l7 7 7-7" fill="none" stroke="#132630" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
          </g>
        </svg>
      </div>
      <div className="col an-up-copy">
        <h1>{t('ast.update.title')}</h1>
        <p className="muted">{mode === 'force' ? t('ast.update.force') : t('ast.update.optional')}</p>
        <ul>
          {ITEMS.map(([icon, key], i) => (
            <li key={key} style={{ animationDelay: `${500 + i * 180}ms` }}>
              <Icon name={icon} size={20} />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
        <div className="row">
          {mode === 'optional' && onClose && (
            <Button variant="ghost" onClick={onClose} data-testid="app-update-later">
              {t('maybeLater')}
            </Button>
          )}
          <Button onClick={update} data-testid="app-update-cta">
            {t('ast.update.cta')}
          </Button>
        </div>
      </div>
    </div>
  );
}

let dismissedThisSession = false;

/** Mount once at the app root. Shows nothing unless the live config asks for an update. */
export function AppUpdateGate() {
  const cfg = useApp((s) => s.config.appUpdate);
  const [dismissed, setDismissed] = useState(dismissedThisSession);
  const need = cfg ? appUpdateRequirement(APP_BUILD, cfg) : 'none';
  if (need === 'none' || (need === 'optional' && dismissed)) return null;
  return (
    <AppUpdateAnnouncement
      mode={need}
      onClose={() => {
        dismissedThisSession = true;
        setDismissed(true);
      }}
    />
  );
}
